import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { RetiredBadge } from "@/components/admin/retired-badge";
import { LEVEL_CONFIG, type CountryLevel } from "@/lib/countries";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Facilities.
 *
 * Port of src/app/admin/facilities/{page,facilities-client}.tsx (legacy
 * NextJS, ~970 LOC client) per D21 NO REINVENTION.
 *
 * Optimizations declared (Architecture Discipline Rule 1 + D4):
 *   - raw fetch / useAdminFetch hook → apiFetch (Architecture Rule 1)
 *   - next-intl useTranslations → Lingui Trans/t macros (D4)
 *   - useSearchParams + useRouter from next/navigation → TanStack Router
 *     validateSearch + useNavigate
 *   - useState/useEffect for fetch → useQuery (shared cache, auto-refetch)
 *   - showToast → imperative toast helper (lib/ui/toast.ts)
 *
 * Endpoints consumed (apps/api):
 *   GET /api/admin/facilities (list, paginated)
 *   GET /api/admin/facilities/summary (countries + categories breakdown)
 *   GET /api/admin/facilities/:id (full detail)
 *   DELETE /api/admin/facilities/:id (soft-delete with retention countdown)
 *   POST /api/admin/facilities/:id/cancel-deletion
 */

interface CountryInfo {
  code: string;
  name: Record<string, string>;
  flag: string;
  facilities: number;
  providers?: number;
  platformStatus?: string;
  totalProviders?: number;
  totalFacilities?: number;
  totalClaimed?: number;
}

interface CategoryInfo {
  category: string;
  count: number;
}

interface Facility {
  id: string;
  name: Record<string, string>;
  category: string;
  type: string;
  addressCity: string;
  addressCountry: string;
  isClaimed: boolean;
  isVerified: boolean;
  planTier: string;
  ratingAvg: number | null;
  providerCount: number;
  dataSources: unknown;
  confidenceScore: number | null;
  lastScrapedAt: string | null;
  /** Row status: "active", "deleted", "draft", or "retired" (no source lists it any more). */
  status?: string;
  createdAt: string;
  pendingDeletion: { scheduledHardDeleteAt: string; daysRemaining: number } | null;
  pendingClaim: { id: string; createdAt: string } | null;
}

interface FacilityDetail extends Facility {
  description: Record<string, string> | null;
  addressStreet: string | null;
  addressPostal: string | null;
  addressCounty: string | null;
  addressFormatted: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string[];
  email: string | null;
  website: string | null;
  openingHours: unknown;
  hasParking: boolean | null;
  hasWheelchairAccess: boolean | null;
  hasElevator: boolean | null;
  hasAccessibleRestroom: boolean | null;
  hasAirConditioning: boolean | null;
  hasWifi: boolean | null;
  hasChildrenArea: boolean | null;
  hasBreastfeedingArea: boolean | null;
  hasPublicTransport: boolean | null;
  publicTransportInfo: string | null;
  acceptsPublicInsurance: boolean;
  emergencyServices: boolean;
  languages: string[];
  claimedByUserId: string | null;
  claimedAt: string | null;
  maxProviders: number | null;
  parentFacilityId: string | null;
  branchName: string | null;
  reviewCount: number;
  photos: string[];
  googlePlaceId: string | null;
  showPhone: boolean;
  showEmail: boolean;
  profileViews: number;
  phoneReveals: number;
  searchAppearances: number;
  status: string;
  updatedAt: string;
}

const categoryLabels: Record<string, { icon: string; label: string }> = {
  hospitals: { icon: "🏥", label: "Hospitals" },
  clinics: { icon: "🏢", label: "Clinics" },
  dental: { icon: "🦷", label: "Dental" },
  pharmacies: { icon: "💊", label: "Pharmacies" },
  diagnostics: { icon: "🔬", label: "Diagnostics" },
  imaging: { icon: "📡", label: "Imaging" },
  rehabilitation: { icon: "♿", label: "Rehabilitation" },
};

function displayName(name: Record<string, string>): string {
  return name.ro || name.en || Object.values(name).find((v) => v && !v.startsWith("_")) || "—";
}

function formatSources(dataSources: unknown): string {
  if (!dataSources) return "—";
  if (Array.isArray(dataSources)) {
    return dataSources
      .map((s: { source?: string }) => s.source || "unknown")
      .filter((v, i, a) => a.indexOf(v) === i)
      .join(", ");
  }
  return "—";
}

function getCountryLevel(country: {
  platformStatus?: string;
  totalFacilities?: number;
  totalProviders?: number;
  totalClaimed?: number;
  providersWithSchedule?: number;
}): CountryLevel {
  if (!country.platformStatus || country.platformStatus === "hidden") return "hidden";
  if (country.platformStatus === "coming_soon") return "coming_soon";
  const claimed = country.totalClaimed ?? 0;
  const withSchedule = country.providersWithSchedule ?? 0;
  if (withSchedule >= 50) return "booking";
  if (claimed >= 1) return "directory";
  return "seeding";
}

interface FacilitiesSearch {
  country?: string;
  category?: string;
  claimed?: string;
  deleted?: string;
}

const PAGE_SIZE = 50;

export const Route = createFileRoute("/_authenticated/facilities")({
  validateSearch: (search: Record<string, unknown>): FacilitiesSearch => ({
    country: typeof search.country === "string" ? search.country : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
    claimed: typeof search.claimed === "string" ? search.claimed : undefined,
    deleted: typeof search.deleted === "string" ? search.deleted : undefined,
  }),
  component: FacilitiesPage,
});

function FacilitiesPage() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [selectedCountry, setSelectedCountry] = useState<string>(search.country ?? "");
  const [selectedCategory, setSelectedCategory] = useState<string>(search.category ?? "");
  const [claimedFilter, setClaimedFilter] = useState<string>(search.claimed ?? "");
  const [deletedFilter, setDeletedFilter] = useState<string>(search.deleted ?? "hide");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [viewFacilityId, setViewFacilityId] = useState<string | null>(null);

  const applyFilters = (
    country = selectedCountry,
    category = selectedCategory,
    claimed = claimedFilter,
    deleted = deletedFilter,
  ) => {
    setSelectedCountry(country);
    setSelectedCategory(category);
    setClaimedFilter(claimed);
    setDeletedFilter(deleted);
    setPage(0);
    void navigate({
      search: () => ({
        country: country || undefined,
        category: category || undefined,
        claimed: claimed || undefined,
        deleted: deleted && deleted !== "hide" ? deleted : undefined,
      }),
      replace: true,
    });
  };

  const summaryQuery = useQuery({
    queryKey: ["admin", "facilities", "summary"],
    queryFn: async (): Promise<{
      countriesWithData: CountryInfo[];
      allCountries: CountryInfo[];
      categories: CategoryInfo[];
    }> => {
      try {
        return await apiFetch.get("/api/admin/facilities/summary");
      } catch (error) {
        if (error instanceof ApiError) {
          return { countriesWithData: [], allCountries: [], categories: [] };
        }
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });

  const countriesWithData = summaryQuery.data?.countriesWithData ?? [];
  const allCountries = summaryQuery.data?.allCountries ?? [];
  const categories = summaryQuery.data?.categories ?? [];

  useEffect(() => {
    if (selectedCountry) return;
    if (countriesWithData.length === 0) return;
    setSelectedCountry(countriesWithData[0].code);
  }, [selectedCountry, countriesWithData]);

  const facilitiesQuery = useQuery({
    queryKey: [
      "admin",
      "facilities",
      "list",
      selectedCountry,
      selectedCategory,
      claimedFilter,
      deletedFilter,
      page,
    ],
    queryFn: async (): Promise<{ facilities: Facility[]; total: number }> => {
      try {
        const params = new URLSearchParams();
        if (selectedCountry) params.set("country", selectedCountry);
        if (selectedCategory) params.set("category", selectedCategory);
        if (claimedFilter) params.set("claimed", claimedFilter);
        if (deletedFilter && deletedFilter !== "hide") params.set("deleted", deletedFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(page * PAGE_SIZE));
        const data = await apiFetch.get<{ facilities: Facility[]; total: number }>(
          `/api/admin/facilities?${params.toString()}`,
        );
        return { facilities: data.facilities ?? [], total: data.total ?? 0 };
      } catch (error) {
        if (error instanceof ApiError) return { facilities: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
    enabled: summaryQuery.isSuccess,
  });

  const facilities = facilitiesQuery.data?.facilities ?? [];
  const total = facilitiesQuery.data?.total ?? 0;

  const filteredFacilities = useMemo(() => {
    if (!searchQuery) return facilities;
    const q = searchQuery.toLowerCase();
    return facilities.filter((f) => {
      const name = displayName(f.name).toLowerCase();
      const city = (f.addressCity || "").toLowerCase();
      return name.includes(q) || city.includes(q);
    });
  }, [searchQuery, facilities]);

  const cancelFacilityDeletion = async (facilityId: string) => {
    setCancelingId(facilityId);
    try {
      await apiFetch.post(`/api/admin/facilities/${facilityId}/cancel-deletion`);
      void facilitiesQuery.refetch();
    } catch (error) {
      const msg = error instanceof ApiError ? `${error.status}` : "Unknown error";
      showToast(t`Cancel failed: ${msg}`, "error");
    } finally {
      setCancelingId(null);
    }
  };

  const deleteFacility = async (facilityId: string, name: string) => {
    const ok = confirm(
      t`Delete facility "${name}"? It will be queued for deletion — hard-delete runs automatically after 30 days, cancelable until then.`,
    );
    if (!ok) return;
    setCancelingId(facilityId);
    try {
      await apiFetch.delete(`/api/admin/facilities/${facilityId}`);
      void facilitiesQuery.refetch();
    } catch (error) {
      const msg = error instanceof ApiError ? `${error.status}` : "Unknown error";
      showToast(t`Delete failed: ${msg}`, "error");
    } finally {
      setCancelingId(null);
    }
  };

  const detailQuery = useQuery({
    queryKey: ["admin", "facilities", "detail", viewFacilityId],
    queryFn: async (): Promise<FacilityDetail | null> => {
      if (!viewFacilityId) return null;
      try {
        return await apiFetch.get<FacilityDetail>(`/api/admin/facilities/${viewFacilityId}`);
      } catch (error) {
        if (error instanceof ApiError) return null;
        throw error;
      }
    },
    enabled: !!viewFacilityId,
    staleTime: 60_000,
  });

  const viewFacility = detailQuery.data ?? null;
  const viewLoading = detailQuery.isLoading && !!viewFacilityId;

  const countryInfo = countriesWithData.find((c) => c.code === selectedCountry);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (summaryQuery.isLoading && facilities.length === 0) {
    return (
      <div className="text-muted-foreground p-8 text-sm">
        <Trans>Loading facilities…</Trans>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Facilities</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            {countryInfo ? (
              <Trans>
                Facility directory — {total.toLocaleString()} facilities in{" "}
                {countryInfo.name.en || countryInfo.code}
              </Trans>
            ) : (
              <Trans>Facility directory — {total.toLocaleString()} facilities</Trans>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-muted-foreground text-xs font-medium">
          <Trans>Country</Trans>
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => applyFilters("")}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
              selectedCountry === ""
                ? "border-primary bg-primary/10 text-primary ring-primary/40 ring-2 ring-offset-1"
                : "border-border text-muted-foreground opacity-70 hover:opacity-100"
            }`}
          >
            <span>🌍</span>
            <Trans>All</Trans>
            <span className="text-muted-foreground text-xs">
              ({countriesWithData.reduce((s, c) => s + c.facilities, 0).toLocaleString()})
            </span>
          </button>
          {countriesWithData.map((c) => {
            const level = getCountryLevel({
              platformStatus: c.platformStatus || "open",
              totalFacilities: c.totalFacilities || c.facilities,
              totalProviders: c.totalProviders || c.providers || 0,
              totalClaimed: c.totalClaimed || 0,
            });
            const cfg = LEVEL_CONFIG[level];
            return (
              <button
                key={c.code}
                onClick={() => applyFilters(c.code)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                  selectedCountry === c.code
                    ? `ring-2 ring-offset-1 ${cfg?.color || "border-primary bg-primary/10 text-primary"}`
                    : `${cfg?.color || "border-border text-muted-foreground"} opacity-70 hover:opacity-100`
                }`}
              >
                {cfg && <span className={`inline-block h-2 w-2 rounded-full ${cfg.dot}`} />}
                <span>{c.flag}</span>
                <span>{c.name.en || c.code}</span>
                <span className="text-muted-foreground text-xs">
                  ({c.facilities.toLocaleString()})
                </span>
              </button>
            );
          })}

          {allCountries.filter((c) => !countriesWithData.find((d) => d.code === c.code)).length >
            0 && (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) applyFilters(e.target.value);
              }}
              className="border-border text-muted-foreground rounded-full border px-3 py-1.5 text-sm"
            >
              <option value="">{t`Other countries…`}</option>
              {allCountries
                .filter((c) => !countriesWithData.find((d) => d.code === c.code))
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.name.en || c.code}
                  </option>
                ))}
            </select>
          )}
        </div>
      </div>

      {countryInfo && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            emoji="🏢"
            value={countryInfo.facilities.toLocaleString()}
            label={<Trans>Total facilities</Trans>}
            active={claimedFilter === ""}
            onClick={() => applyFilters(undefined, undefined, "")}
          />
          <StatCard
            emoji="✅"
            value={facilities.filter((f) => f.isClaimed).length}
            label={<Trans>Claimed</Trans>}
            active={claimedFilter === "true"}
            onClick={() =>
              applyFilters(undefined, undefined, claimedFilter === "true" ? "" : "true")
            }
          />
          <StatCard
            emoji="📋"
            value={facilities.filter((f) => !f.isClaimed).length}
            label={<Trans>Unclaimed</Trans>}
            active={claimedFilter === "false"}
            onClick={() =>
              applyFilters(undefined, undefined, claimedFilter === "false" ? "" : "false")
            }
          />
          <StatCard
            emoji="📨"
            value={facilities.filter((f) => f.pendingClaim).length}
            label={<Trans>Claim sent</Trans>}
            active={claimedFilter === "claim_pending"}
            onClick={() =>
              applyFilters(
                undefined,
                undefined,
                claimedFilter === "claim_pending" ? "" : "claim_pending",
              )
            }
            tone="amber"
          />
          <StatCard
            emoji="🩺"
            value={(countryInfo.providers || 0).toLocaleString()}
            label={<Trans>Providers</Trans>}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder={t`Search facility or city…`}
          className="max-w-xs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          value={selectedCategory}
          onChange={(e) => applyFilters(undefined, e.target.value)}
          className="border-border bg-card rounded-lg border px-3 py-2 text-sm"
        >
          <option value="">{t`All categories`}</option>
          {categories.map((c) => (
            <option key={c.category} value={c.category}>
              {(categoryLabels[c.category]?.icon || "🏢") +
                " " +
                (categoryLabels[c.category]?.label || c.category)}{" "}
              ({c.count})
            </option>
          ))}
        </select>
        <select
          value={claimedFilter}
          onChange={(e) => applyFilters(undefined, undefined, e.target.value)}
          className="border-border bg-card rounded-lg border px-3 py-2 text-sm"
        >
          <option value="">{t`All statuses`}</option>
          <option value="true">{t`Claimed`}</option>
          <option value="false">{t`Unclaimed`}</option>
          <option value="claim_pending">{t`Claim sent (pending)`}</option>
        </select>
        <select
          value={deletedFilter}
          onChange={(e) => applyFilters(undefined, undefined, undefined, e.target.value)}
          className="border-border bg-card rounded-lg border px-3 py-2 text-sm"
        >
          <option value="hide">{t`Not deleted`}</option>
          <option value="all">{t`All (incl. deleted)`}</option>
          <option value="only">{t`Only deleted`}</option>
          <option value="pending">{t`Marked for deletion`}</option>
          <option value="retired">{t`Retired (no longer listed by the source)`}</option>
        </select>
        {facilitiesQuery.isFetching && (
          <span className="text-muted-foreground self-center text-xs">
            <Trans>Loading…</Trans>
          </span>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>Facility</Trans>
                </TableHead>
                <TableHead className="hidden sm:table-cell">
                  <Trans>Category</Trans>
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  <Trans>City</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Status</Trans>
                </TableHead>
                <TableHead className="hidden sm:table-cell">
                  <Trans>Plan</Trans>
                </TableHead>
                <TableHead className="hidden text-right md:table-cell">
                  <Trans>Providers</Trans>
                </TableHead>
                <TableHead className="hidden text-right lg:table-cell">
                  <Trans>Rating</Trans>
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  <Trans>Source</Trans>
                </TableHead>
                <TableHead className="w-8 md:hidden" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFacilities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-muted-foreground py-12 text-center">
                    {facilitiesQuery.isLoading ? (
                      <Trans>Loading facilities…</Trans>
                    ) : selectedCountry ? (
                      <Trans>No facilities found</Trans>
                    ) : (
                      <Trans>Select a country to view facilities</Trans>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFacilities.map((f) => (
                  <ExpandableRow
                    key={f.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    mobileDetails={[
                      {
                        label: <Trans>Category</Trans>,
                        value: (
                          <>
                            <span className="mr-1">{categoryLabels[f.category]?.icon || "🏢"}</span>
                            <span className="text-muted-foreground text-xs">
                              {f.type.replace(/_/g, " ")}
                            </span>
                          </>
                        ),
                      },
                      {
                        label: <Trans>Plan</Trans>,
                        value: <Badge variant="secondary">{f.planTier}</Badge>,
                      },
                      { label: <Trans>City</Trans>, value: f.addressCity || "—" },
                      { label: <Trans>Providers</Trans>, value: f.providerCount },
                      {
                        label: <Trans>Rating</Trans>,
                        value: f.ratingAvg ? Number(f.ratingAvg).toFixed(1) : "—",
                      },
                      { label: <Trans>Source</Trans>, value: formatSources(f.dataSources) },
                    ]}
                  >
                    <TableCell
                      className="max-w-[250px] truncate font-medium"
                      onClick={() => setViewFacilityId(f.id)}
                    >
                      {displayName(f.name)}
                      {f.status === "retired" && (
                        <div className="mt-1">
                          <RetiredBadge />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="mr-1">{categoryLabels[f.category]?.icon || "🏢"}</span>
                      <span className="text-muted-foreground text-xs">
                        {f.type.replace(/_/g, " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden md:table-cell">
                      {f.addressCity || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        {f.isClaimed && f.isVerified ? (
                          <Badge
                            variant="outline"
                            className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                          >
                            <Trans>Verified</Trans>
                          </Badge>
                        ) : f.isClaimed ? (
                          <Badge
                            variant="outline"
                            className="border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-300"
                          >
                            <Trans>Claimed</Trans>
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <Trans>Unclaimed</Trans>
                          </Badge>
                        )}
                        {f.pendingClaim && (
                          <a
                            href={`/claims?open=${f.pendingClaim.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex"
                            title={t`Claim sent ${new Date(f.pendingClaim.createdAt).toLocaleDateString()} — click for review`}
                          >
                            <Badge className="bg-amber-100 text-xs text-amber-800 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300">
                              <Trans>Claim sent</Trans>
                            </Badge>
                          </a>
                        )}
                        {f.pendingDeletion && (
                          <Badge className="bg-red-100 text-xs text-red-700">
                            <Trans>Delete in {f.pendingDeletion.daysRemaining}d</Trans>
                          </Badge>
                        )}
                        {f.pendingDeletion ? (
                          <button
                            className="text-xs font-medium text-emerald-700 underline hover:text-emerald-800 disabled:opacity-50"
                            disabled={cancelingId === f.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void cancelFacilityDeletion(f.id);
                            }}
                          >
                            {cancelingId === f.id ? "..." : <Trans>Cancel</Trans>}
                          </button>
                        ) : (
                          <button
                            className="text-xs font-medium text-red-600 underline hover:text-red-700 disabled:opacity-50"
                            disabled={cancelingId === f.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void deleteFacility(f.id, displayName(f.name));
                            }}
                          >
                            {cancelingId === f.id ? "..." : <Trans>Delete</Trans>}
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary">{f.planTier}</Badge>
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">
                      {f.providerCount}
                    </TableCell>
                    <TableCell className="hidden text-right lg:table-cell">
                      {f.ratingAvg ? Number(f.ratingAvg).toFixed(1) : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden max-w-[120px] truncate text-xs lg:table-cell">
                      {formatSources(f.dataSources)}
                    </TableCell>
                  </ExpandableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            <Trans>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of{" "}
              {total.toLocaleString()}
            </Trans>
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <Trans>Previous</Trans>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <Trans>Next</Trans>
            </Button>
          </div>
        </div>
      )}

      <Sheet
        open={viewFacility !== null || viewLoading}
        onOpenChange={(open) => {
          if (!open) setViewFacilityId(null);
        }}
      >
        <SheetContent className="overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {viewFacility ? displayName(viewFacility.name) : <Trans>Loading…</Trans>}
            </SheetTitle>
          </SheetHeader>

          {viewLoading && !viewFacility && (
            <div className="text-muted-foreground py-12 text-center text-sm">
              <Trans>Loading…</Trans>
            </div>
          )}

          {viewFacility && (
            <div className="mt-4 space-y-6">
              <DetailSection title={<Trans>Name</Trans>}>
                <LangGrid data={viewFacility.name} />
              </DetailSection>

              {viewFacility.description && (
                <DetailSection title={<Trans>Description</Trans>}>
                  <LangGrid data={viewFacility.description} />
                </DetailSection>
              )}

              <DetailSection title={<Trans>Category</Trans>}>
                <div className="flex items-center gap-2">
                  <span>{categoryLabels[viewFacility.category]?.icon || "🏢"}</span>
                  <span className="font-medium">{viewFacility.category}</span>
                  <span className="text-muted-foreground">
                    / {viewFacility.type.replace(/_/g, " ")}
                  </span>
                </div>
              </DetailSection>

              <DetailSection title={<Trans>Facility</Trans>}>
                <DetailRow label={<Trans>Street</Trans>} value={viewFacility.addressStreet} />
                <DetailRow label={<Trans>City</Trans>} value={viewFacility.addressCity} />
                <DetailRow label={<Trans>County</Trans>} value={viewFacility.addressCounty} />
                <DetailRow label={<Trans>Postal code</Trans>} value={viewFacility.addressPostal} />
                <DetailRow label={<Trans>Country</Trans>} value={viewFacility.addressCountry} />
                <DetailRow label={<Trans>Formatted</Trans>} value={viewFacility.addressFormatted} />
                {viewFacility.latitude && viewFacility.longitude && (
                  <DetailRow
                    label={<Trans>Coordinates</Trans>}
                    value={`${viewFacility.latitude.toFixed(5)}, ${viewFacility.longitude.toFixed(5)}`}
                  />
                )}
              </DetailSection>

              <DetailSection title={<Trans>Contact</Trans>}>
                <DetailRow
                  label={<Trans>Phone</Trans>}
                  value={viewFacility.phone.length > 0 ? viewFacility.phone.join(", ") : null}
                />
                <DetailRow label={<Trans>Email</Trans>} value={viewFacility.email} />
                <DetailRow label={<Trans>Website</Trans>} value={viewFacility.website} />
                {viewFacility.openingHours != null && (
                  <div>
                    <span className="text-muted-foreground text-xs">
                      <Trans>Opening hours</Trans>
                    </span>
                    <pre className="bg-muted mt-1 rounded p-2 text-xs">
                      {JSON.stringify(viewFacility.openingHours, null, 2)}
                    </pre>
                  </div>
                )}
              </DetailSection>

              <DetailSection title={<Trans>Amenities & services</Trans>}>
                <div className="flex flex-wrap gap-2">
                  {viewFacility.hasParking && (
                    <Badge variant="secondary">
                      <Trans>Parking</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasWheelchairAccess && (
                    <Badge variant="secondary">
                      <Trans>Wheelchair access</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasElevator && (
                    <Badge variant="secondary">
                      <Trans>Elevator</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasAccessibleRestroom && (
                    <Badge variant="secondary">
                      <Trans>Accessible restroom</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasPublicTransport && (
                    <Badge variant="secondary">
                      <Trans>Public transport</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasChildrenArea && (
                    <Badge variant="secondary">
                      <Trans>Children area</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasBreastfeedingArea && (
                    <Badge variant="secondary">
                      <Trans>Breastfeeding area</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasAirConditioning && (
                    <Badge variant="secondary">
                      <Trans>Air conditioning</Trans>
                    </Badge>
                  )}
                  {viewFacility.hasWifi && (
                    <Badge variant="secondary">
                      <Trans>WiFi</Trans>
                    </Badge>
                  )}
                  {viewFacility.acceptsPublicInsurance && (
                    <Badge variant="secondary">
                      <Trans>Accepts CAS</Trans>
                    </Badge>
                  )}
                  {viewFacility.emergencyServices && (
                    <Badge variant="secondary">
                      <Trans>Emergency services</Trans>
                    </Badge>
                  )}
                </div>
                <DetailRow
                  label={<Trans>Public transport</Trans>}
                  value={viewFacility.publicTransportInfo}
                />
                <DetailRow
                  label={<Trans>Languages</Trans>}
                  value={
                    viewFacility.languages.length > 0 ? viewFacility.languages.join(", ") : null
                  }
                />
              </DetailSection>

              <DetailSection title={<Trans>Status & plan</Trans>}>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={viewFacility.isClaimed ? "default" : "outline"}>
                    {viewFacility.isClaimed ? <Trans>Claimed</Trans> : <Trans>Unclaimed</Trans>}
                  </Badge>
                  <Badge variant={viewFacility.isVerified ? "default" : "outline"}>
                    {viewFacility.isVerified ? <Trans>Verified</Trans> : <Trans>Unverified</Trans>}
                  </Badge>
                  <Badge variant="secondary">{viewFacility.planTier}</Badge>
                  <Badge variant="secondary">{viewFacility.status}</Badge>
                </div>
                <DetailRow label={<Trans>Claimed by</Trans>} value={viewFacility.claimedByUserId} />
                <DetailRow
                  label={<Trans>Claimed at</Trans>}
                  value={
                    viewFacility.claimedAt
                      ? new Date(viewFacility.claimedAt).toLocaleDateString()
                      : null
                  }
                />
                <DetailRow
                  label={<Trans>Max providers</Trans>}
                  value={viewFacility.maxProviders?.toString() ?? null}
                />
              </DetailSection>

              <DetailSection title={<Trans>Statistics</Trans>}>
                <div className="grid grid-cols-3 gap-3">
                  <StatBox label={<Trans>Providers</Trans>} value={viewFacility.providerCount} />
                  <StatBox label={<Trans>Reviews</Trans>} value={viewFacility.reviewCount} />
                  <StatBox
                    label={<Trans>Rating</Trans>}
                    value={viewFacility.ratingAvg ? Number(viewFacility.ratingAvg).toFixed(1) : "—"}
                  />
                  <StatBox label={<Trans>Profile views</Trans>} value={viewFacility.profileViews} />
                  <StatBox label={<Trans>Phone reveals</Trans>} value={viewFacility.phoneReveals} />
                  <StatBox
                    label={<Trans>Search appearances</Trans>}
                    value={viewFacility.searchAppearances}
                  />
                </div>
              </DetailSection>

              {viewFacility.photos.length > 0 && (
                <DetailSection title={<Trans>Photos ({viewFacility.photos.length})</Trans>}>
                  <div className="flex flex-wrap gap-2">
                    {viewFacility.photos.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-xs underline"
                      >
                        <Trans>Photo {i + 1}</Trans>
                      </a>
                    ))}
                  </div>
                </DetailSection>
              )}

              <DetailSection title={<Trans>Data sources</Trans>}>
                <DetailRow
                  label={<Trans>Google Place ID</Trans>}
                  value={viewFacility.googlePlaceId}
                />
                <DetailRow
                  label={<Trans>Confidence</Trans>}
                  value={
                    viewFacility.confidenceScore
                      ? `${(Number(viewFacility.confidenceScore) * 100).toFixed(0)}%`
                      : null
                  }
                />
                <DetailRow
                  label={<Trans>Last scraped</Trans>}
                  value={
                    viewFacility.lastScrapedAt
                      ? new Date(viewFacility.lastScrapedAt).toLocaleString()
                      : null
                  }
                />
                {viewFacility.dataSources != null && (
                  <div>
                    <span className="text-muted-foreground text-xs">
                      <Trans>Sources</Trans>
                    </span>
                    <pre className="bg-muted mt-1 max-h-40 overflow-auto rounded p-2 text-xs">
                      {JSON.stringify(viewFacility.dataSources, null, 2)}
                    </pre>
                  </div>
                )}
              </DetailSection>

              <DetailSection title={<Trans>Timestamps</Trans>}>
                <DetailRow
                  label={<Trans>Created</Trans>}
                  value={new Date(viewFacility.createdAt).toLocaleString()}
                />
                <DetailRow
                  label={<Trans>Updated</Trans>}
                  value={new Date(viewFacility.updatedAt).toLocaleString()}
                />
              </DetailSection>

              <div className="text-muted-foreground border-t pt-3 font-mono text-[0.625rem]">
                ID: {viewFacility.id}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailSection({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground shrink-0 text-xs">{label}</span>
      <span className="text-right text-sm">{value}</span>
    </div>
  );
}

function StatBox({ label, value }: { label: React.ReactNode; value: string | number }) {
  return (
    <div className="bg-muted/50 rounded-lg p-2.5 text-center">
      <div className="text-sm font-bold">{value}</div>
      <div className="text-muted-foreground text-[0.625rem]">{label}</div>
    </div>
  );
}

function LangGrid({ data }: { data: Record<string, string> }) {
  const entries = Object.entries(data).filter(([k]) => !k.startsWith("_"));
  return (
    <div className="grid grid-cols-2 gap-2">
      {entries.map(([lang, val]) => (
        <div key={lang} className="bg-muted/50 rounded-lg px-3 py-1.5">
          <span className="text-muted-foreground text-[0.625rem] font-medium uppercase">
            {lang}
          </span>
          <div className="text-sm">{val || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function StatCard({
  emoji,
  value,
  label,
  active,
  onClick,
  tone,
}: {
  emoji: string;
  value: string | number;
  label: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  tone?: "amber";
}) {
  const ring = active
    ? tone === "amber"
      ? "ring-2 ring-amber-500 dark:ring-amber-400"
      : "ring-2 ring-teal-500 dark:ring-teal-400"
    : "";
  const interactive = onClick ? "cursor-pointer hover:border-teal-400 transition-colors" : "";
  const content = (
    <CardContent className="flex items-center gap-3 p-4">
      <span className="text-xl">{emoji}</span>
      <div>
        <div className="text-lg font-bold">{value}</div>
        <div className="text-muted-foreground text-xs">{label}</div>
      </div>
    </CardContent>
  );
  if (!onClick) {
    return <Card className={ring}>{content}</Card>;
  }
  return (
    <Card
      className={`${ring} ${interactive}`.trim()}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {content}
    </Card>
  );
}
